/**
 * Geo policy — single source of truth for jurisdictional gating.
 *
 * The lists below implement Schedule 1 of the Foundation Director's written
 * resolution of 22 September 2026 (Jurisdictional Access Schedule and
 * Amendment of Continuing Condition 5(a)), which is whitepaper Appendix F.7
 * and is explained jurisdiction by jurisdiction in
 * docs/Legal/QSAVE_GEO_BUCKET_IMPLEMENTATION_INSTRUCTION.md.
 *
 * Five POLICY BUCKETS by site behaviour:
 *
 * 1. FULL-SITE BLOCK — no website access. Sanctions law: Cuba, Iran, North
 *    Korea, Russia, and the OFAC-designated occupied territories of Ukraine.
 * 2. TOKEN INFORMATION OFF — protocol information stays available; token
 *    pages (/tokenomics, /token/*, /docs/Tokenomics/*), contract addresses
 *   , and the redacted Public Information
 *    Edition is served in place of the full whitepaper. Three legally
 *    distinct sources feed this bucket and are kept as separate lists:
 *      - MICA_RESTRICTED_COUNTRIES  (EU 27 + EEA 3 + UK)
 *      - CRYPTO_RESTRICTED_COUNTRIES (local-law restrictions)
 *      - TOKEN_INTERACTION_BLOCKED   (South Korea: reporting regime for
 *        foreign virtual-asset businesses; issuer rules not yet law)
 *    plus, within the United States, the state of New York
 *    (TOKEN_INTERACTION_BLOCKED_US_STATES).
 * 3. US UTILITY ACCESS — token information and official contract addresses
 *    visible; no issuer capital raise to US persons.
 *    The United States except New York, including California, Louisiana and
 *    Pennsylvania (removed from bucket 2 on 22 September 2026).
 * 4. OPEN — token information and contract addresses visible. The default for any code not named in another bucket, and for a
 *    missing or non-country code. OPEN_COUNTRIES lists the jurisdictions the
 *    Schedule names expressly; it is documentation, not a gate.
 *
 * Buckets 3, 4 and 5 render identically on the website today; the
 * distinction exists for the whitepaper's US disclosures, for the staking
 *
 * The website itself does NO active token sale. The buckets exist to (a) avoid inadvertent
 * solicitation in jurisdictions where it would be problematic, and (b) match
 * the disclosure commitments made in the QSAVE whitepaper.
 *
 * Country codes are ISO 3166-1 alpha-2.
 *
 * NOTE: When updating these lists, ALSO update the root /vercel.json (Edge
 * redirect rules for buckets 1 and 2) and /api/geo-check.ts (serverless
 * mirror; it cannot import this module). The three files change together.
 * (The root vercel.json is the active Vercel config — its build command
 * cd's into qkey-react-v2; the former qkey-react-v2/vercel.json was removed.)
 */

// Bucket 1. Sanctioned countries — site fully blocked. Sanctions/AML rules.
export const SANCTIONED_COUNTRIES = [
  'CU', // Cuba
  'IR', // Iran
  'KP', // North Korea
  'RU', // Russia
] as const;

// Bucket 1. OFAC-designated occupied territories of Ukraine. Best-effort
// filtering at the edge by ISO 3166-2:UA subdivision code. Operator-side OFAC
// screening at any token-interaction or financial onboarding step provides
// the authoritative enforcement layer; this is a defensive supplement.
export const SANCTIONED_UA_REGIONS = [
  '43', // Crimea (Autonomous Republic)
  '40', // Sevastopol (city with special status)
  '09', // Luhansk Oblast
  '14', // Donetsk Oblast
  '23', // Zaporizhzhia Oblast
  '65', // Kherson Oblast
] as const;

// Bucket 2. Jurisdictions whose token-interaction surfaces are blocked on a
// regulatory-regime basis that is neither sanctions, local-law prohibition
// nor MiCA/FCA.
// - South Korea: foreign virtual-asset businesses serving Korean residents
//   must report under the Specified Financial Transaction Information Act;
//   the Digital Asset Basic Act that would set issuer rules is not yet law.
//
// India and Japan left this list on 22 September 2026 (now bucket 4, see
// OPEN_COUNTRIES). The United States is NOT in this list: it is bucket 3 at
// federal level, with New York gated by TOKEN_INTERACTION_BLOCKED_US_STATES.
//
// To re-block the US at federal level if regulatory posture changes:
// add 'US' to this list. The change propagates through VERCEL_REGEX
// helpers; vercel.json must be edited in parallel.
export const TOKEN_INTERACTION_BLOCKED = [
  'KR', // South Korea
] as const;

// Bucket 2 within the United States. Filtered via x-vercel-ip-country-region
// when country=US.
//
// The authoritative set is NY only, per QSAVE_GEOFENCING_KYC_POLICY.md
// section A.2 as revised by the Foundation's resolution of 22 September 2026.
// California, Louisiana and Pennsylvania were removed on that date on the
// no-custody, no-transmission facts and are now bucket 3 with the rest of the
// United States. New York stays on the issuer limb of 23 NYCRR Part 200.
export const TOKEN_INTERACTION_BLOCKED_US_STATES = [
  'NY', // New York — BitLicense (23 NYCRR Part 200, issuer limb) + Martin Act
] as const;

// Bucket 2. Crypto-restricted jurisdictions. These countries have hard local
// crypto bans or strong restrictions under their own domestic law — they are
// NOT on US sanctions lists. Listed separately from SANCTIONED_COUNTRIES
// because the legal category is distinct (local law restrictions vs. US
// sanctions enforcement).
export const CRYPTO_RESTRICTED_COUNTRIES = [
  'CN', // China
  'BD', // Bangladesh
  'NP', // Nepal
  'DZ', // Algeria
  'MA', // Morocco
  'EG', // Egypt
] as const;
// Bolivia (BO) left this list on 25 May 2026: Banco Central de Bolivia lifted
// its crypto ban in June 2024; open since 22 September 2026. Pakistan (PK)
// left on 22 September 2026: the Virtual Assets Act 2026 replaced the
// local-law ban with a licensing regime; bucket 4. Keep quoted codes out of
// comments inside these array literals: scripts/generate-jurisdiction-md.mjs
// harvests every quoted two-letter code between the brackets.

// Bucket 2. MiCA-restricted jurisdictions (EU 27 + EEA 3 + UK).
// MiCA (Markets in Crypto-Assets Regulation), effective December 2024,
// requires a notified crypto-asset white paper before an offer to the public,
// and a token page with contract addresses is that communication. UK has
// analogous FCA financial-promotion rules. Until QSAVE completes MiCA / FCA
// registration, these jurisdictions:
//   - Cannot access token-interaction pages (/tokenomics, /token/*,
//     /docs/Tokenomics/*) — redirected to /geo-blocked
//   - Receive the QSAVE Public Information Edition (redacted) instead of
//     the full whitepaper
export const MICA_RESTRICTED_COUNTRIES = [
  // EU 27
  'AT', // Austria
  'BE', // Belgium
  'BG', // Bulgaria
  'HR', // Croatia
  'CY', // Cyprus
  'CZ', // Czech Republic
  'DK', // Denmark
  'EE', // Estonia
  'FI', // Finland
  'FR', // France
  'DE', // Germany
  'GR', // Greece
  'HU', // Hungary
  'IE', // Ireland
  'IT', // Italy
  'LV', // Latvia
  'LT', // Lithuania
  'LU', // Luxembourg
  'MT', // Malta
  'NL', // Netherlands
  'PL', // Poland
  'PT', // Portugal
  'RO', // Romania
  'SK', // Slovakia
  'SI', // Slovenia
  'ES', // Spain
  'SE', // Sweden
  // EEA additional
  'IS', // Iceland
  'LI', // Liechtenstein
  'NO', // Norway
  // UK (FCA financial-promotion rules; analogous regime to MiCA)
  'GB', // United Kingdom
] as const;

// Bucket 4. Open. Documentation only: bucket 4 is the
// default, so nothing gates on this list. These are the jurisdictions the
// Schedule names expressly, each moved out of bucket 2 on 22 September 2026.
// so links stay off. Reasons per jurisdiction are in
// docs/Legal/QSAVE_GEO_BUCKET_IMPLEMENTATION_INSTRUCTION.md.
//
// NOTE: 'CA' here is CANADA (ISO 3166-1 alpha-2 country). It does NOT collide
// with a US region code — US regions are checked only when country === 'US'
// (see TOKEN_INTERACTION_BLOCKED_US_STATES).
export const OPEN_COUNTRIES = [
  'AU', // Australia (ASIC INFO 225; AFS licensing for arranging)
  'SG', // Singapore (MAS Payment Services Act / FSMA DPT services)
  'HK', // Hong Kong (SFC AMLO regime; SFO s.103)
  'AE', // United Arab Emirates (VARA Marketing Regulations 2024)
  'TH', // Thailand (Emergency Decree on Digital Asset Businesses (No. 2) B.E. 2568)
  'IN', // India (FIU-India registration for VDA service providers)
  'JP', // Japan (Payment Services Act; FIEA amendment pending)
  'PK', // Pakistan (Virtual Assets Act 2026; s.61 reaches unlicensed-service links)
  'CA', // Canada (CSA investment-contract analysis pending)
  'CH',
  'BR',
  'BO',
  'SY',
] as const;


// Restricted whitepaper jurisdictions — POLICY BUCKET (combines categories).
// These users get redirected to the QSAVE Public Information Edition
// (informational, non-offering) when they request the full whitepaper PDF.
// Combines every bucket-2 country list plus bucket 1:
//   - CRYPTO_RESTRICTED_COUNTRIES (local-law crypto bans)
//   - SANCTIONED_COUNTRIES (US sanctions)
//   - MICA_RESTRICTED_COUNTRIES (EU/EEA + UK MiCA / FCA)
//   - TOKEN_INTERACTION_BLOCKED (South Korea)
// The source lists are legally distinct; this combined list exists
// because the policy outcome (redirect to public edition) is the same.
export const RESTRICTED_WHITEPAPER_COUNTRIES = [
  ...CRYPTO_RESTRICTED_COUNTRIES,
  ...SANCTIONED_COUNTRIES,
  ...MICA_RESTRICTED_COUNTRIES,
  ...TOKEN_INTERACTION_BLOCKED,
] as const;

// Helpers

export type CountryCode = string;
export type RegionCode = string;

export function isSanctioned(
  country?: CountryCode | null,
  region?: RegionCode | null
): boolean {
  if (!country) return false;
  if ((SANCTIONED_COUNTRIES as readonly string[]).includes(country)) return true;
  if (country === 'UA' && region && (SANCTIONED_UA_REGIONS as readonly string[]).includes(region)) {
    return true;
  }
  return false;
}

// True for buckets 1 and 2: the token-interaction surfaces are hidden.
export function isTokenInteractionBlocked(
  country?: CountryCode | null,
  region?: RegionCode | null
): boolean {
  if (!country) return false;
  if ((TOKEN_INTERACTION_BLOCKED as readonly string[]).includes(country)) return true;
  if ((MICA_RESTRICTED_COUNTRIES as readonly string[]).includes(country)) return true;
  if ((CRYPTO_RESTRICTED_COUNTRIES as readonly string[]).includes(country)) return true;
  if (isSanctioned(country, region)) return true;
  if (country === 'US' && region && (TOKEN_INTERACTION_BLOCKED_US_STATES as readonly string[]).includes(region)) {
    return true;
  }
  return false;
}

export function isWhitepaperRestricted(
  country?: CountryCode | null,
  region?: RegionCode | null
): boolean {
  if (!country) return false;
  if ((RESTRICTED_WHITEPAPER_COUNTRIES as readonly string[]).includes(country)) return true;
  // US: state-level restriction also gates whitepaper access
  if (country === 'US' && region && (TOKEN_INTERACTION_BLOCKED_US_STATES as readonly string[]).includes(region)) {
    return true;
  }
  return false;
}

// Pipe-joined regex string for use in vercel.json `has.value` matching.
// Generated for documentation; vercel.json must be edited by hand to match.
export const VERCEL_REGEX = {
  SANCTIONED: SANCTIONED_COUNTRIES.join('|'),
  SANCTIONED_UA_REGIONS: SANCTIONED_UA_REGIONS.join('|'),
  CRYPTO_RESTRICTED: CRYPTO_RESTRICTED_COUNTRIES.join('|'),
  MICA_RESTRICTED: MICA_RESTRICTED_COUNTRIES.join('|'),
  TOKEN_INTERACTION_COUNTRIES: [
    ...TOKEN_INTERACTION_BLOCKED,
    ...CRYPTO_RESTRICTED_COUNTRIES,
    ...SANCTIONED_COUNTRIES,
    ...MICA_RESTRICTED_COUNTRIES,
  ].join('|'),
  TOKEN_INTERACTION_US_STATES: TOKEN_INTERACTION_BLOCKED_US_STATES.join('|'),
  RESTRICTED_WHITEPAPER: RESTRICTED_WHITEPAPER_COUNTRIES.join('|'),
} as const;
